import React, { useEffect, useState } from "react";
import { X, Save, Plus, Trash2, Camera } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import CameraCapture from "@/components/CameraCapture";

/**
 * Full editable contract modal for staff (admin/station_manager).
 * Allows editing time, vehicle, price, deposit, payment method, customer fields,
 * defects note, odometer values, and adding extra photos.
 */
const ContractEditModal = ({ open, contract, vehicles, stations = [], onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [extras, setExtras] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && contract) {
      setForm({
        vehicle_id: contract.vehicle_id || "",
        rental_type: contract.rental_type || "vito_daily",
        rental_start: (contract.rental_start || "").slice(0, 16) || "",
        rental_end: (contract.rental_end || "").slice(0, 16) || "",
        destination: contract.destination || "ch",
        days: contract.days || contract.duration_days || 1,
        package_hours: contract.package_hours || 3,
        expected_km: contract.expected_km ?? "",
        overtime_hours: contract.overtime_hours ?? 0,
        price_chf: contract.price_chf ?? "",
        deposit_chf: contract.deposit_chf ?? 300,
        deductible_chf: contract.deductible_chf ?? 1000,
        payment_method: contract.payment_method || "cash",
        defects_note: contract.defects_note || "",
        notes: contract.notes || "",
        odometer_start: contract.odometer_start ?? "",
        odometer_end: contract.odometer_end ?? "",
        station_id: contract.station_id || "",
        customer: { ...(contract.customer || {}) },
      });
      setExtras([]);
    }
  }, [open, contract]);

  if (!open || !contract) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCust = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      const keys = ["vehicle_id", "rental_type", "rental_start", "rental_end",
                    "destination", "package_hours", "expected_km", "overtime_hours",
                    "price_chf", "deposit_chf", "deductible_chf", "payment_method", "defects_note",
                    "notes", "odometer_start", "odometer_end", "station_id"];
      keys.forEach((k) => {
        const v = form[k];
        if (v === "" || v === null || v === undefined) return;
        body[k] = (k === "price_chf" || k === "deposit_chf" || k === "deductible_chf" || k === "overtime_hours") ? parseFloat(v)
                : (k === "odometer_start" || k === "odometer_end" || k === "package_hours" || k === "expected_km") ? parseInt(v, 10)
                : v;
      });
      // Only send `days` when start+end are NOT both set — otherwise let backend
      // recompute the day count from the date range. This avoids stale `days`
      // overriding the new period after extending rental_end.
      if (!body.rental_start || !body.rental_end) {
        if (form.days !== "" && form.days != null) body.days = parseInt(form.days, 10);
      }
      // If the user changed the rental period (vs. the originally loaded contract),
      // remove the stale `price_chf` from the payload so the backend recomputes it
      // from the new dates. The admin can always set a custom price separately.
      const startChanged = (form.rental_start || "") !== ((contract.rental_start || "").slice(0, 16));
      const endChanged = (form.rental_end || "") !== ((contract.rental_end || "").slice(0, 16));
      const priceUnchanged = parseFloat(form.price_chf) === parseFloat(contract.price_chf);
      if ((startChanged || endChanged) && priceUnchanged) {
        delete body.price_chf;
      }
      if (form.customer && form.customer.full_name) {
        body.customer = form.customer;
      }
      if (extras.length > 0) {
        body.additional_photos = extras;
      }
      await api.patch(`/contracts/${contract.id}`, body);
      toast.success("Vertrag aktualisiert");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="contract-edit-modal">
      <div className="bg-white border-4 border-[var(--rs-primary)] w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--rs-border)] bg-[var(--rs-primary)] text-white">
          <div>
            <div className="rs-overline !text-white/60">{contract.contract_number}</div>
            <h3 className="font-[Chivo] text-2xl font-black mt-1">Vertrag bearbeiten</h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white" data-testid="edit-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Vehicle + Period */}
          <section>
            <div className="rs-overline mb-3">Fahrzeug & Zeitraum</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="rs-label">Fahrzeug</span>
                <select
                  className="rs-input"
                  value={form.vehicle_id ?? ""}
                  onChange={(e) => setField("vehicle_id", e.target.value)}
                  data-testid="edit-vehicle"
                >
                  {(vehicles || []).map((v) => (
                    <option key={v.id} value={v.id}>{`${v.name} (${v.plate})`}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="rs-label">Tarif</span>
                <select
                  className="rs-input"
                  value={form.rental_type ?? "vito_daily"}
                  onChange={(e) => setField("rental_type", e.target.value)}
                  data-testid="edit-rental-type"
                >
                  <option value="vito_daily">Vito · Tagesmiete</option>
                  <option value="master_package">Master · Paket</option>
                </select>
              </label>
              <label className="block">
                <span className="rs-label">Mietbeginn</span>
                <input type="datetime-local" step="60" className="rs-input"
                  value={form.rental_start} onChange={(e) => setField("rental_start", e.target.value)}
                  data-testid="edit-start" />
              </label>
              <label className="block">
                <span className="rs-label">Mietende</span>
                <input type="datetime-local" step="60" className="rs-input"
                  value={form.rental_end} onChange={(e) => setField("rental_end", e.target.value)}
                  data-testid="edit-end" />
              </label>
              <label className="block md:col-span-2">
                <span className="rs-label">Abholort / Rückgabeort</span>
                <select
                  className="rs-input"
                  value={form.station_id ?? ""}
                  onChange={(e) => setField("station_id", e.target.value)}
                  data-testid="edit-station"
                >
                  <option value="">— unverändert —</option>
                  {(stations || []).map((s) => (
                    <option key={s.id} value={s.id}>{`${s.name} · ${s.full}`}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          {/* Price + Payment */}
          <section>
            <div className="rs-overline mb-3">Preis & Zahlung</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <label className="block">
                <span className="rs-label">Preis CHF</span>
                <input type="number" step="0.05" className="rs-input"
                  value={form.price_chf} onChange={(e) => setField("price_chf", e.target.value)}
                  data-testid="edit-price" />
              </label>
              <label className="block">
                <span className="rs-label">Kaution CHF</span>
                <input type="number" step="1" className="rs-input"
                  value={form.deposit_chf} onChange={(e) => setField("deposit_chf", e.target.value)}
                  data-testid="edit-deposit" />
              </label>
              <label className="block">
                <span className="rs-label">Selbstbehalt</span>
                <select className="rs-input"
                  value={form.deductible_chf} onChange={(e) => setField("deductible_chf", e.target.value)}
                  data-testid="edit-deductible">
                  <option value={1000}>{"CHF 1'000 (≥ 25 Jahre)"}</option>
                  <option value={2000}>{"CHF 2'000 (< 25 Jahre)"}</option>
                </select>
              </label>
              <label className="block">
                <span className="rs-label">Zahlungsart</span>
                <select className="rs-input"
                  value={form.payment_method} onChange={(e) => setField("payment_method", e.target.value)}
                  data-testid="edit-payment">
                  <option value="cash">Barzahlung</option>
                  <option value="twint">TWINT</option>
                  <option value="bank">Banküberweisung</option>
                </select>
              </label>
            </div>
          </section>

          {/* Billing (payments + extra charges + settle) */}
          <BillingSection contract={contract} onChanged={onSaved} />

          {/* Customer */}
          <section>
            <div className="rs-overline mb-3">Kundendaten</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="rs-input" placeholder="Name" value={form.customer?.full_name || ""}
                onChange={(e) => setCust("full_name", e.target.value)} data-testid="edit-cust-name" />
              <input className="rs-input" placeholder="Führerscheinnummer" value={form.customer?.license_number || ""}
                onChange={(e) => setCust("license_number", e.target.value)} data-testid="edit-cust-license" />
              <input className="rs-input" placeholder="Adresse" value={form.customer?.address || ""}
                onChange={(e) => setCust("address", e.target.value)} data-testid="edit-cust-address" />
              <div className="grid grid-cols-3 gap-3">
                <input className="rs-input" placeholder="PLZ" value={form.customer?.postal_code || ""}
                  onChange={(e) => setCust("postal_code", e.target.value)} data-testid="edit-cust-postal" />
                <input className="rs-input col-span-2" placeholder="Ort" value={form.customer?.city || ""}
                  onChange={(e) => setCust("city", e.target.value)} data-testid="edit-cust-city" />
              </div>
              <input className="rs-input" placeholder="Telefon" value={form.customer?.phone || ""}
                onChange={(e) => setCust("phone", e.target.value)} data-testid="edit-cust-phone" />
              <input className="rs-input" placeholder="E-Mail" value={form.customer?.email || ""}
                onChange={(e) => setCust("email", e.target.value)} data-testid="edit-cust-email" />
            </div>
          </section>

          {/* Odometer + Defects */}
          <section>
            <div className="rs-overline mb-3">KM-Stand & Mängel</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="rs-label">KM-Stand Start</span>
                <input type="number" className="rs-input"
                  value={form.odometer_start} onChange={(e) => setField("odometer_start", e.target.value)}
                  data-testid="edit-odo-start" />
              </label>
              <label className="block">
                <span className="rs-label">KM-Stand Ende</span>
                <input type="number" className="rs-input"
                  value={form.odometer_end} onChange={(e) => setField("odometer_end", e.target.value)}
                  data-testid="edit-odo-end" />
              </label>
            </div>
            <label className="block mt-3">
              <span className="rs-label">Mängel / Bemerkungen</span>
              <textarea rows={3} className="rs-input min-h-[80px] resize-y"
                value={form.defects_note} onChange={(e) => setField("defects_note", e.target.value)}
                data-testid="edit-defects" />
            </label>
          </section>

          {/* Additional photos */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="rs-overline">Zusätzliche Fotos hinzufügen</div>
              <button type="button" className="rs-btn-ghost !text-xs" onClick={() => setCameraOpen(true)} data-testid="edit-add-photo">
                <Plus size={14} className="mr-1" /> Foto
              </button>
            </div>
            {extras.length === 0 ? (
              <div className="text-xs text-[var(--rs-text-muted)] border border-dashed border-[var(--rs-border)] py-4 px-3 text-center">
                Keine neuen Fotos. Bestehende Fotos im PDF bleiben erhalten.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {extras.map((src, i) => (
                  <div key={i} className="relative aspect-square bg-[var(--rs-surface)] border border-[var(--rs-border)] overflow-hidden">
                    <img src={src} alt={`extra-${i}`} className="w-full h-full object-cover" />
                    <button type="button"
                      onClick={() => setExtras((arr) => arr.filter((_, idx) => idx !== i))}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 hover:bg-red-700">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="p-5 border-t border-[var(--rs-border)] flex justify-between gap-3">
          <button type="button" className="rs-btn-secondary !py-3 !text-xs" onClick={onClose}>Abbrechen</button>
          <button type="button" className="rs-btn-primary !py-3 !text-xs disabled:opacity-50"
            onClick={save} disabled={saving} data-testid="edit-save-btn">
            <Save size={14} className="mr-2" /> {saving ? "Speichert…" : "Änderungen speichern"}
          </button>
        </div>
      </div>

      {cameraOpen && (
        <CameraCapture
          onCancel={() => setCameraOpen(false)}
          onCapture={(d) => { setExtras((arr) => [...arr, d]); setCameraOpen(false); }}
        />
      )}
    </div>
  );
};

export default ContractEditModal;

// ──────────────────────────────────────────────────────────────────────
// BillingSection: payments ledger + extra charges + settle button
// ──────────────────────────────────────────────────────────────────────
const fmtChf = (n) => {
  const v = Number(n || 0);
  return Math.abs(v - Math.round(v)) < 0.005
    ? Math.round(v).toLocaleString("de-CH").replace(/,/g, "'") + ".–"
    : v.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, "'");
};

const METHOD_LABEL = { cash: "Bar", twint: "TWINT", bank: "Bank" };
const KIND_LABEL = { deposit: "Anzahlung", final: "Restzahlung", payment: "Zahlung" };

const BillingSection = ({ contract, onChanged }) => {
  const [data, setData] = useState(contract);
  const [adding, setAdding] = useState(false);
  const [addingExtra, setAddingExtra] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "cash", kind: "payment", note: "" });
  const [extra, setExtra] = useState({ label: "", amount: "", note: "", photo: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setData(contract); }, [contract]);

  const refresh = async () => {
    try {
      const { data: c } = await api.get(`/contracts/${contract.id}`);
      setData(c);
      onChanged && onChanged();
    } catch (e) {
      // non-fatal: list view will refresh on next modal open
    }
  };

  const payments = data.payments || [];
  const extras = data.extra_charges || [];
  const extraTotal = extras.reduce((s, x) => s + Number(x.amount || 0), 0);
  const paidTotal = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalDue = Number(data.price_chf || 0) + extraTotal;
  const openBalance = Math.max(0, totalDue - paidTotal);
  const fullyPaid = totalDue > 0 && openBalance <= 0.01;

  const submitPay = async () => {
    const amount = parseFloat(pay.amount);
    if (!amount || amount <= 0) { toast.error("Betrag fehlt"); return; }
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/payments`, {
        amount, method: pay.method, kind: pay.kind, note: pay.note || null,
      });
      toast.success("Zahlung erfasst");
      setPay({ amount: "", method: "cash", kind: "payment", note: "" });
      setAdding(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setBusy(false); }
  };

  const delPay = async (pid) => {
    if (!window.confirm("Zahlung wirklich löschen?")) return;
    try { await api.delete(`/contracts/${contract.id}/payments/${pid}`); await refresh(); }
    catch { toast.error("Fehler"); }
  };

  const submitExtra = async () => {
    const amount = parseFloat(extra.amount);
    if (!amount || amount <= 0 || !extra.label) { toast.error("Bezeichnung & Betrag fehlen"); return; }
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/extra-charges`, {
        label: extra.label, amount, note: extra.note || null,
        photo_base64: extra.photo || null,
      });
      toast.success("Zusatzkosten erfasst");
      setExtra({ label: "", amount: "", note: "", photo: "" });
      setAddingExtra(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setBusy(false); }
  };

  const onExtraPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Foto zu gross (max. 6 MB)"); return; }
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    img.onload = () => {
      const maxW = 1280;
      const scale = img.width > maxW ? maxW / img.width : 1;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      setExtra((prev) => ({ ...prev, photo: dataUrl }));
    };
    reader.readAsDataURL(f);
  };

  const delExtra = async (xid) => {
    if (!window.confirm("Zusatzkosten wirklich löschen?")) return;
    try { await api.delete(`/contracts/${contract.id}/extra-charges/${xid}`); await refresh(); }
    catch { toast.error("Fehler"); }
  };

  const settle = async () => {
    if (!window.confirm("Vertrag jetzt als abgerechnet abschliessen?")) return;
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/settle`, { confirm: true });
      toast.success("Vertrag abgerechnet & abgeschlossen");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Abschluss");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="billing-section">
      <div className="rs-overline mb-3">Abrechnung</div>

      {/* Totals card */}
      <div className="bg-[var(--rs-surface)] border-l-4 border-[var(--rs-primary)] p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rs-text-muted)]">Vertrag</div>
          <div className="font-[Chivo] text-lg font-black">CHF {fmtChf(data.price_chf || 0)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rs-text-muted)]">Zusatzkosten</div>
          <div className="font-[Chivo] text-lg font-black text-[#B91C1C]">CHF {fmtChf(extraTotal)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rs-text-muted)]">Bezahlt</div>
          <div className="font-[Chivo] text-lg font-black text-[var(--rs-primary)]">CHF {fmtChf(paidTotal)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rs-text-muted)]">Offen</div>
          <div className={`font-[Chivo] text-lg font-black ${fullyPaid ? "text-[var(--rs-primary)]" : "text-[#B91C1C]"}`}>
            {fullyPaid ? "✓ Bezahlt" : `CHF ${fmtChf(openBalance)}`}
          </div>
        </div>
      </div>

      {/* Payments list */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wider">Zahlungen ({payments.length})</div>
          <button type="button" onClick={() => setAdding((v) => !v)} className="rs-btn-ghost !text-xs" data-testid="add-payment-btn">
            <Plus size={12} className="mr-1" /> Zahlung
          </button>
        </div>
        {payments.length === 0 ? (
          <div className="text-xs text-[var(--rs-text-muted)] italic px-2">Noch keine Zahlung erfasst.</div>
        ) : (
          <div className="space-y-1">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-white border border-[var(--rs-border)] px-3 py-2 text-sm">
                <div>
                  <span className="font-bold">{KIND_LABEL[p.kind] || "Zahlung"}</span>{" "}
                  · CHF {fmtChf(p.amount)} · {METHOD_LABEL[p.method] || p.method}
                  <span className="text-xs text-[var(--rs-text-muted)] ml-2">{(p.paid_at || "").slice(0, 10).split("-").reverse().join(".")}</span>
                  {p.note && <span className="text-xs text-[var(--rs-text-muted)] ml-2">· {p.note}</span>}
                </div>
                <button type="button" onClick={() => delPay(p.id)} className="text-[#B91C1C] hover:opacity-70" data-testid={`del-payment-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {adding && (
          <div className="mt-3 bg-[var(--rs-surface)] border border-[var(--rs-primary)] p-3 grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="payment-form">
            <select className="rs-input !py-2 !text-sm" value={pay.kind} onChange={(e) => setPay({ ...pay, kind: e.target.value })}>
              <option value="deposit">Anzahlung</option>
              <option value="payment">Zahlung</option>
              <option value="final">Restzahlung</option>
            </select>
            <input type="number" step="0.05" min="0" placeholder="Betrag CHF"
              className="rs-input !py-2 !text-sm" value={pay.amount}
              onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" />
            <select className="rs-input !py-2 !text-sm" value={pay.method}
              onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="payment-method-sel">
              <option value="cash">Bar</option>
              <option value="twint">TWINT</option>
              <option value="bank">Bank</option>
            </select>
            <input className="rs-input !py-2 !text-sm" placeholder="Notiz (optional)"
              value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} />
            <button type="button" onClick={submitPay} disabled={busy}
              className="rs-btn-primary !py-2 !text-xs" data-testid="payment-save-btn">
              Speichern
            </button>
          </div>
        )}
      </div>

      {/* Extra charges */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wider">Zusatzkosten / Schäden ({extras.length})</div>
          <button type="button" onClick={() => setAddingExtra((v) => !v)} className="rs-btn-ghost !text-xs" data-testid="add-extra-btn">
            <Plus size={12} className="mr-1" /> Zusatzkosten
          </button>
        </div>
        {extras.length === 0 ? (
          <div className="text-xs text-[var(--rs-text-muted)] italic px-2">Keine Zusatzkosten.</div>
        ) : (
          <div className="space-y-1">
            {extras.map((x) => (
              <div key={x.id} className="flex items-start justify-between bg-white border border-[var(--rs-border)] px-3 py-2 text-sm gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {x.photo_base64 && (
                    <img src={x.photo_base64} alt={x.label}
                      className="w-14 h-14 object-cover rounded-md border border-[var(--rs-border)] flex-shrink-0"
                      data-testid={`extra-photo-${x.id}`} />
                  )}
                  <div className="min-w-0">
                    <div className="font-bold truncate">{x.label} · CHF {fmtChf(x.amount)}</div>
                    {x.note && <div className="text-xs text-[var(--rs-text-muted)]">{x.note}</div>}
                  </div>
                </div>
                <button type="button" onClick={() => delExtra(x.id)} className="text-[#B91C1C] hover:opacity-70 flex-shrink-0" data-testid={`del-extra-${x.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {addingExtra && (
          <div className="mt-3 bg-[var(--rs-surface)] border border-[#B91C1C] p-3 grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="extra-form">
            <input className="rs-input !py-2 !text-sm sm:col-span-2" placeholder="z. B. Kratzer Stossstange, Reinigung…"
              value={extra.label} onChange={(e) => setExtra({ ...extra, label: e.target.value })} data-testid="extra-label" />
            <input type="number" step="0.05" min="0" placeholder="Betrag CHF"
              className="rs-input !py-2 !text-sm" value={extra.amount}
              onChange={(e) => setExtra({ ...extra, amount: e.target.value })} data-testid="extra-amount" />
            <button type="button" onClick={submitExtra} disabled={busy}
              className="rs-btn-primary !py-2 !text-xs" data-testid="extra-save-btn">
              Speichern
            </button>
            <input className="rs-input !py-2 !text-sm sm:col-span-4" placeholder="Notiz (optional)"
              value={extra.note} onChange={(e) => setExtra({ ...extra, note: e.target.value })} />
            <div className="sm:col-span-4 flex items-center gap-3 mt-1">
              <label className="rs-btn-secondary !py-2 !text-xs cursor-pointer" data-testid="extra-photo-pick">
                <Camera size={14} className="mr-1 inline" />
                {extra.photo ? "Foto ändern" : "Foto hinzufügen (optional)"}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onExtraPhoto} />
              </label>
              {extra.photo && (
                <>
                  <img src={extra.photo} alt="preview" className="w-14 h-14 object-cover rounded-md border border-[var(--rs-border)]" data-testid="extra-photo-preview" />
                  <button type="button" onClick={() => setExtra({ ...extra, photo: "" })} className="text-xs text-[#B91C1C] underline">
                    entfernen
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Settle */}
      <div className="mt-5 flex items-center justify-between border-t border-[var(--rs-border)] pt-3">
        <div className="text-xs text-[var(--rs-text-muted)]">
          {fullyPaid
            ? "Vertrag kann jetzt abgerechnet & abgeschlossen werden."
            : `Offen: CHF ${fmtChf(openBalance)} – Vertrag kann nach vollständiger Zahlung abgeschlossen werden.`}
        </div>
        <button type="button" onClick={settle}
          disabled={!fullyPaid || busy || data.status === "completed"}
          className="rs-btn-primary !py-3 !text-xs disabled:opacity-40"
          data-testid="settle-btn">
          {data.status === "completed" ? "✓ Bereits abgerechnet" : "Abrechnen & abschliessen"}
        </button>
      </div>
    </section>
  );
};
